import {
  AbodeAggregateMarketConditions,
  CommentaryV2,
  estimatedDemandSupplyOptions,
  estimatedExposureTimeOptions,
  expected,
  marketConditionsCommentaryFilterTypes,
  schMarketConditionsCommentaryFilterType,
} from '@aloft/toolkit-shared';
import {
  Block,
  Button,
  Flex,
  Icon,
  Input,
  RadioGroup,
  Select,
  Typography,
} from '@aloft/toolkit-sui';

import { useEffect, useState } from 'react';
import { FieldPath, useFormContext } from 'react-hook-form';

import { FormTextarea } from '../../../components/FormTextarea/FormTextarea.tsx';
import { useGetMarketAggregateDataAPI } from '../../../hooks/api/toolkitComponents.ts';
import { useAppraisifyValidation } from '../../../hooks/validation/useAppraisifyValidation.ts';
import { addMarketAggregateData } from '../../../redux/features/orderSlice.ts';
import { selectMarketAggregateData } from '../../../redux/features/selectors/commentarySelectors/selectCurrentMarketAggregateData.ts';
import {
  selectEffectiveDate,
  selectOrder,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { selectBackupListingDataById } from '../../../redux/features/selectors/propertyDataSelectors/backupListingDataSelectors.ts';
import { selectPropertyIdentificationById } from '../../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { RequiredFieldAnchor } from '../../alerts/RequiredFieldAnchor.tsx';
import { MarketAdjustments } from '../../commentary/MarketAdjustments/MarketAdjustments.tsx';
import { MarketConditionsGraphs } from '../components/MarketConditionsGraphs.tsx';
import { isEstimatedExposureTimeInconsistentWithDom } from '../utils/getEstimatedExposureTimeDays.ts';
import { getGeoIdForSelectedGeoType } from '../utils/getGeoIdForSelectedGeoType.ts';
import { parseMarketConditionsGeoType } from '../utils/parseMarketConditionsGeoType.ts';

type ArrayWithFindLast<T> = T[] & {
  findLast(predicate: (value: T) => boolean): T | undefined;
};

export const MarketConditionsTabPanels = () => null;

MarketConditionsTabPanels.LeftPanel = () => {
  const { setValue, watch, control } = useFormContext<CommentaryV2>();
  const { id } = useAppSelector(selectOrder);
  const effectiveDate = useAppSelector(selectEffectiveDate);
  const propertyIdentification = useAppSelector((state) =>
    selectPropertyIdentificationById(state, 'subject'),
  );
  const readOnlyListingData = useAppSelector((state) =>
    selectBackupListingDataById(state, 'subject'),
  );
  const dispatch = useAppDispatch();
  const allMarketAggregateData = useAppSelector((state) => selectMarketAggregateData(state));
  const { errors } = useAppraisifyValidation();
  const { marketConditionCommentary } = errors?.Commentary ?? undefined;
  const commentaryFieldName: FieldPath<CommentaryV2> = 'marketConditionsV2.commentary';
  const estimatedDemandSupplyFieldName: FieldPath<CommentaryV2> =
    'marketConditionsV2.estimatedDemandSupply';
  const estimatedExposureTimeFieldName: FieldPath<CommentaryV2> =
    'marketConditionsV2.estimatedExposureTime';
  const filterTypeFieldName: FieldPath<CommentaryV2> = 'marketConditionsV2.filter.type';
  const filterValueFieldName: FieldPath<CommentaryV2> = 'marketConditionsV2.filter.value';

  const estimatedDemandSupply = watch(estimatedDemandSupplyFieldName);
  const estimatedExposureTime = watch(estimatedExposureTimeFieldName);
  const filterType = watch(filterTypeFieldName);
  const filterValue = watch(filterValueFieldName);
  const parsedGeoType = parseMarketConditionsGeoType(filterType);
  const parsedGeoId = getGeoIdForSelectedGeoType(
    parsedGeoType,
    filterValue,
    propertyIdentification,
    readOnlyListingData,
  );
  const [zipCodeValue, setZipCodeValue] = useState<string>(
    filterValue ?? propertyIdentification?.address.PostalCode?.substring(0, 5) ?? '',
  );

  const { data, isLoading } = useGetMarketAggregateDataAPI({
    pathParams: { orderID: id },
    queryParams: {
      geoType: parsedGeoType,
      geoId: parsedGeoId,
    },
  });
  const marketAggregateData = data as AbodeAggregateMarketConditions;
  const monthlyData = marketAggregateData?.monthlyData as
    | ArrayWithFindLast<AbodeAggregateMarketConditions['monthlyData'][number]>
    | undefined;
  const effectiveMonth = effectiveDate?.isoString.substring(0, 7);
  const effectiveMonthDaysOnMarket = monthlyData?.find(
    (item) => item.month.substring(0, 7) === effectiveMonth && item.median_days_on_market !== null,
  )?.median_days_on_market;
  const latestDaysOnMarket = monthlyData?.findLast(
    (item) => item.median_days_on_market !== null,
  )?.median_days_on_market;
  const daysOnMarket = effectiveMonthDaysOnMarket ?? latestDaysOnMarket;
  const showExposureWarning = isEstimatedExposureTimeInconsistentWithDom(
    estimatedExposureTime,
    daysOnMarket ?? undefined,
  );

  useEffect(() => {
    // Workaround to update Redux when new data is fetched
    if (
      !isLoading &&
      marketAggregateData &&
      !allMarketAggregateData?.find(
        (data) =>
          data.geoType === marketAggregateData.geoType && data.geoId === marketAggregateData.geoId,
      )
    ) {
      dispatch(addMarketAggregateData(marketAggregateData));
    }
  }, [marketAggregateData, allMarketAggregateData, dispatch, isLoading]);

  const onApplyZipCodeValue = () => {
    setValue(filterValueFieldName, zipCodeValue);
  };

  return (
    <Flex flexDirection="column" padding="1rem" gap="1.5rem" width="100%">
      <Typography variant="h2">Market Condition Analysis</Typography>
      <Flex flexDirection="column" gap="1rem">
        <Flex gap="2rem">
          <Flex whiteSpace="nowrap">
            <RadioGroup
              isRequired
              size="md"
              label="Filter By:"
              items={marketConditionsCommentaryFilterTypes.map((value) => ({
                label: value,
                value,
              }))}
              value={filterType}
              onChange={(v) =>
                setValue(filterTypeFieldName, schMarketConditionsCommentaryFilterType.parse(v))
              }
            />
          </Flex>
          <Flex flexGrow={1} alignItems="center" gap="0.5rem">
            <Input
              type="text"
              size="xs"
              value={zipCodeValue}
              onChange={(v) => setZipCodeValue(v)}
              onClear={() => setZipCodeValue('')}
            />
            <Button
              variant="primary"
              size="sm"
              text="Apply"
              disabled={zipCodeValue.length !== 5 || zipCodeValue === filterValue}
              onClick={onApplyZipCodeValue}
            />
          </Flex>
        </Flex>
      </Flex>
      <RequiredFieldAnchor fieldName="estimatedDemand">
        <Select
          isRequired
          label="Estimated Demand / Supply"
          value={estimatedDemandSupply}
          options={estimatedDemandSupplyOptions.map((value) => ({ label: value, value }))}
          onChange={(v) => setValue(estimatedDemandSupplyFieldName, expected(v))}
        />
      </RequiredFieldAnchor>
      <RequiredFieldAnchor fieldName="estimatedExposureTime">
        <Block width="100%">
          <Select
            isRequired
            label="Estimated Exposure Time"
            value={estimatedExposureTime}
            infoText="The amount of time the property would have been exposed to the open market before a buyer and seller, each reasonably motivated and well-informed, agreed on the appraised market value. It assumes normal marketing efforts and typical market conditions prevailing during that period."
            options={estimatedExposureTimeOptions.map((value) => ({ label: value, value }))}
            onChange={(v) => setValue(estimatedExposureTimeFieldName, expected(v))}
            __dangerDoNotUseSx={showExposureWarning ? { borderColor: 'statusWarning' } : undefined}
          />
          {showExposureWarning && (
            <Flex
              alignItems="flex-start"
              gap="0.5rem"
              marginTop="0.5rem"
              width="100%"
              flexShrink={0}
            >
              <Icon icon="error-warning" size="md" color="statusWarning" />
              <Typography
                variant="body1"
                color="textPrimary"
                __dangerDoNotUseSx={{ fontWeight: 700, flex: 1 }}
              >
                Exposure time is inconsistent with the reported Median Comparable DOM (
                {daysOnMarket!}). Please reconcile the exposure time with market activity.
              </Typography>
            </Flex>
          )}
        </Block>
      </RequiredFieldAnchor>
      <FormTextarea
        requiredFieldName="marketConditionCommentary"
        name={commentaryFieldName}
        control={control}
        label="Market Condition Commentary"
        placeholder=""
        isRequired
        column
        resize="vertical"
        customErrorMessage={marketConditionCommentary?.message}
        onClear={() => setValue(commentaryFieldName, undefined)}
        labelVariant="label"
        labelColor="textPrimary"
      />
    </Flex>
  );
};

MarketConditionsTabPanels.RightPanel = () => {
  const { id } = useAppSelector(selectOrder);
  const propertyIdentification = useAppSelector((state) =>
    selectPropertyIdentificationById(state, 'subject'),
  );
  const readOnlyListingData = useAppSelector((state) =>
    selectBackupListingDataById(state, 'subject'),
  );
  const { watch } = useFormContext<CommentaryV2>();
  const filterTypeFieldName: FieldPath<CommentaryV2> = 'marketConditionsV2.filter.type';
  const filterValueFieldName: FieldPath<CommentaryV2> = 'marketConditionsV2.filter.value';
  const filterType = watch(filterTypeFieldName);
  const filterValue = watch(filterValueFieldName);
  const parsedGeoType = parseMarketConditionsGeoType(filterType);
  const parsedGeoId = getGeoIdForSelectedGeoType(
    parsedGeoType,
    filterValue,
    propertyIdentification,
    readOnlyListingData,
  );

  const { data, isLoading, isError } = useGetMarketAggregateDataAPI({
    pathParams: { orderID: id },
    queryParams: {
      geoType: parsedGeoType,
      geoId: parsedGeoId,
    },
  });
  const marketAggregateData = data as AbodeAggregateMarketConditions;

  return (
    <>
      <MarketConditionsGraphs
        marketAggregateData={marketAggregateData}
        isLoading={isLoading}
        isError={isError}
      />
      <MarketAdjustments title="market-condition" />
    </>
  );
};
